import { FunctionComponent, useEffect, useState } from 'react';
import { TeamRevenueDetailTable } from './TeamRevenueDetailTable';
import { useDispatch } from 'react-redux';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone } from 'hooks';
import Header from '../../components/Header';
import styles from './TeamRevenue.module.scss';

interface TeamRevenueProps {
    description?: string;
}

interface TeamRevenueData {
    staff: string;
    totalAppointments: number;
    grossSales: number;
    discounts: number;
    refunds: number;
    netSales: number;
    percentOfTotalNetSales: number;
    avgOrderValue: number;
    tips: number;
}

export const TeamRevenue: FunctionComponent<TeamRevenueProps> = props => {
    const dispatch = useDispatch();

    const timeZone = useTimeZone();

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone).startOf('month'),
        to: moment().tz(timeZone).endOf('month')
    });

    const [teamRevenueData, setTeamRevenueData] = useState<TeamRevenueData[] | null>(null);
    const [loading, setLoading] = useState(false);

    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();

    // Mock data for team revenue
    const mockData: TeamRevenueData[] = [
        {
            staff: 'Sarah Johnson',
            totalAppointments: 45,
            grossSales: 5400.0,
            discounts: 200.0,
            refunds: 50.0,
            netSales: 5150.0,
            percentOfTotalNetSales: 35.2,
            avgOrderValue: 120.0,
            tips: 450.0
        },
        {
            staff: 'Mike Chen',
            totalAppointments: 38,
            grossSales: 4560.0,
            discounts: 150.0,
            refunds: 25.0,
            netSales: 4385.0,
            percentOfTotalNetSales: 29.9,
            avgOrderValue: 120.0,
            tips: 320.0
        },
        {
            staff: 'Lisa Rodriguez',
            totalAppointments: 42,
            grossSales: 5040.0,
            discounts: 180.0,
            refunds: 40.0,
            netSales: 4820.0,
            percentOfTotalNetSales: 32.9,
            avgOrderValue: 120.0,
            tips: 380.0
        },
        {
            staff: 'David Wilson',
            totalAppointments: 25,
            grossSales: 3000.0,
            discounts: 100.0,
            refunds: 20.0,
            netSales: 2880.0,
            percentOfTotalNetSales: 19.6,
            avgOrderValue: 120.0,
            tips: 200.0
        }
    ];

    // export
    const teamRevenueCsvHeaders = [
        'Staff',
        'Total Appointments',
        'Gross Sales',
        'Discounts',
        'Refunds',
        'Net Sales',
        '% of total Net Sales',
        'Avg Order Value',
        'Tips'
    ];

    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const dataToExport =
        teamRevenueData?.map(item => [
            item.staff,
            item.totalAppointments.toString(),
            formatCurrency(item.grossSales),
            formatCurrency(item.discounts),
            formatCurrency(item.refunds),
            formatCurrency(item.netSales),
            `${item.percentOfTotalNetSales.toFixed(1)}%`,
            formatCurrency(item.avgOrderValue),
            formatCurrency(item.tips)
        ]) || [];

    const exportFile = {
        name: `Team Revenue - ${marketplace.basics.businessName}.csv`,
        dataInMemory: true,
        dataToExport: dataToExport,
        headers: teamRevenueCsvHeaders
    };

    const calendar = {
        optionSelected: 'This month'
    };

    const [retryAttempt, setRetryAttempt] = useState(0);

    useEffect(() => {
        // Simulate API call
        setLoading(true);
        setTimeout(() => {
            setTeamRevenueData(mockData);
            setLoading(false);
        }, 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        if (allTime) {
            setPeriod(null);
        } else {
            setPeriod({
                from: dateFilter?.from || moment(),
                to: dateFilter?.to || moment()
            });
        }
    };

    return (
        <div
            className={styles.container}
            key={retryAttempt}
        >
            <div>
                <span className={styles.description}>{props.description}</span>
            </div>
            <div className={styles.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                />
            </div>
            <TeamRevenueDetailTable
                loading={loading}
                data={teamRevenueData}
            />
        </div>
    );
};

export default TeamRevenue;
